import {FC, useEffect} from 'react'
import {Modal} from 'react-bootstrap'
import {useDispatch, useSelector} from 'react-redux'
import {RootState} from 'setup'
import {getPaymentMethods, resetPaymentState} from 'app/features/orders/redux/paymentMethodSlice'
import {setShowPaymentCard} from 'app/features/customers/redux/paymentMethodSlice'
import {ModalHeader} from '_core/components/Modal'
import PaymentList from './PaymentList'
import {setPaymentMethod} from '../../redux/draft/draftOrderSlice'
import {getCustomerAddresses} from 'app/features/customers/redux/customerSlice'
import draftOrderPaymentService from 'app/services/DraftOrderPaymentService/DraftOrderPaymentService'
import draftOrderService from 'app/services/DraftOrderService/DraftOrderService'
import {showMessage} from 'app/redux/messageSlice'

type Props = {
  onClose: any
  customerId: any
  selectedPayment?: any
  isSubmitting?: boolean
  error?: any
}

const CustomerPaymentDialog: FC<Props> = ({onClose, customerId, selectedPayment}) => {
  const dispatch = useDispatch()

  const draft = useSelector<RootState>(({orders}) => orders.draft.order) as any

  const paymentMethodList = useSelector<RootState>(
    ({orders}) => orders.paymentMethods.list
  ) as any[]

  const addressList = useSelector<RootState>(
    ({customers}) => customers.customer.customerAddress
  ) as any[]

  const showPayment = useSelector<RootState>(
    ({orders}) => orders.paymentMethods.showAddPaymentCard
  ) as any

  const isFetching = useSelector<RootState>(({orders}) => orders.paymentMethods.isFetching) as any

  const addressIsFetch = useSelector<RootState, boolean>(
    ({customers}) => customers.customer.isFetching
  )

  useEffect(() => {
    if (customerId) {
      dispatch(getPaymentMethods(customerId))
    }
    return () => {
      dispatch(resetPaymentState())
    }
  }, [dispatch, customerId])

  const handleValidate = async (paymentId: any) => {
    return await draftOrderPaymentService.validatePaymentMethod(customerId, paymentId)
  }

  const handleSelectPayment = async (payment: any) => {
    dispatch(setPaymentMethod(payment))
    onClose()
    if (draft && draft.id && !draft.paymentMethodId) {
      await draftOrderService.updateDraftOrder({paymentMethodId: payment.id}, draft.id)
    }
  }

  const handleAddAddress = () => {
    dispatch(setShowPaymentCard(true))
    dispatch(getCustomerAddresses(customerId))
  }

  const handleCardClose = () => {
    if (showPayment) {
      dispatch(setShowPaymentCard(false))
    } else {
      onClose()
    }
  }

  const handleUpdatePaymentMethod = async (paymentMethodId: string) => {
    try {
      await draftOrderPaymentService.updatePrimaryPaymentMethod(paymentMethodId)
      dispatch(getPaymentMethods(customerId))
      dispatch(
        showMessage({
          text: 'Customer Payment has been set as primary successfully.',
          variant: 'success',
        })
      )
    } catch (error) {
      console.log(error)
      dispatch(
        showMessage({
          text: 'An error occurred while set primary payment method',
          variant: 'error',
        })
      )
    }
  }

  return (
    <Modal
      size='lg'
      id='kt_modal_create_app'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='h-auto w-auto'
      show={true}
      centered
    >
      <ModalHeader
        title={showPayment ? 'Add New Payment ' : 'Payment Methods'}
        toolbar={{onClick: onClose}}
      />
      <PaymentList
        paymentList={paymentMethodList}
        addressList={addressList}
        isFetching={isFetching}
        onClose={handleCardClose}
        paymentDialog={showPayment}
        customerId={customerId}
        checkPaymentMethodId={selectedPayment}
        onChangePayment={handleSelectPayment}
        onValidate={handleValidate}
        addressFetching={addressIsFetch}
        onPrimary={handleUpdatePaymentMethod}
      >
        <div className='me-5'>
          <button
            type='button'
            id='btnAddPaymentCard'
            title='Add New Address'
            onClick={handleAddAddress}
            className='btn btn-secondary'
          >
            Add New Payment Method
          </button>
        </div>
      </PaymentList>
    </Modal>
  )
}

export default CustomerPaymentDialog
